import { BASE_RECOVERY_ARGUMENT } from "./recoveryAbi";
import { ethers } from "ethers";
import { ACTIVE_EVM_NETWORK_KEY, EVM_CONFIG } from "../config";

export type PrivateBridgeProvider = "axelar" | "ccip";
export const CCIP_SEPOLIA_SELECTOR = "16015286601757825753";
export const CCIP_SEPOLIA_USDC = "0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238";
export function isQualifiedBridgeAsset(route: any): boolean {
  return (
    route?.provider === "axelar" &&
    route.sourceChainId === 84532 &&
    route.destinationChain === "solana" &&
    route.destinationNetwork === "devnet" &&
    route.decimals === 6 &&
    route.token === "0x036CbD53842c5426634e7929541eC2318f3dCF7e" &&
    route.tokenId ===
      "0xe27dd34618efcfcffe93759e7dc55d507bcbcb9883305514a5e71cc23d32209b" &&
    route.destinationMint === "G6g13ZbjfJqNNiW5Gex329tDP2LFtVfLn8UH6E8aZzhc"
  );
}
export function isQualifiedCcipBridgeAsset(route: any): boolean {
  return (
    route?.provider === "ccip" &&
    route.sourceChainId === 84532 &&
    route.decimals === 6 &&
    route.token === "0x036CbD53842c5426634e7929541eC2318f3dCF7e" &&
    route.destinationChainSelector === CCIP_SEPOLIA_SELECTOR &&
    route.destinationChainId === 11155111 &&
    route.destinationToken === CCIP_SEPOLIA_USDC
  );
}
export const isQualifiedBridgeRoute = (route: any) =>
  isQualifiedBridgeAsset(route) || isQualifiedCcipBridgeAsset(route);

export const PRIVATE_BRIDGE_ROUTE =
  "(address sourcePool,address token,uint256 amount,bytes32 tokenId,string destinationChain,bytes destinationAddress,uint256 gasValue,address refundRecipient,uint64 deadline,bytes32 salt)";
export const PRIVATE_CCIP_BRIDGE_ROUTE =
  "(address sourcePool,address token,uint256 amount,uint64 destinationChainSelector,bytes receiver,bytes extraArgs,uint256 maxFee,address refundRecipient,uint64 deadline,bytes32 salt)";
const LEG =
  "((bytes32 merkleRoot,bytes32[] nullifiers,bytes32[] commitments,address token,address recipient,uint256 publicAmount,bytes proof) settlement,(bytes32 intentHash,bytes32 termsHash,bytes32 nonce,uint64 issuedAt,uint64 deadline,uint8 kind) quote,bytes feeProof,bytes signature)";
export const PRIVATE_BRIDGE_INTERFACE = new ethers.Interface([
  `function execute(${PRIVATE_BRIDGE_ROUTE} route,${LEG} leg,bytes permit${BASE_RECOVERY_ARGUMENT}) payable`,
  `function recover(${PRIVATE_BRIDGE_ROUTE} route,${LEG} leg,bytes permit${BASE_RECOVERY_ARGUMENT})`,
  "event BridgeSubmitted(bytes32 indexed routeHash,address indexed sourcePool,address indexed escrow,address token,uint256 amount,bytes32 tokenId,string destinationChain,bytes destinationAddress)",
]);
export const PRIVATE_CCIP_BRIDGE_INTERFACE = new ethers.Interface([
  `function execute(${PRIVATE_CCIP_BRIDGE_ROUTE} route,${LEG} leg,bytes permit${BASE_RECOVERY_ARGUMENT}) payable returns(bytes32)`,
  `function recover(${PRIVATE_CCIP_BRIDGE_ROUTE} route,${LEG} leg,bytes permit${BASE_RECOVERY_ARGUMENT})`,
  "event BridgeSubmitted(bytes32 indexed routeHash,address indexed sourcePool,address indexed escrow,address token,uint256 amount,uint64 destinationChainSelector,bytes receiver,bytes32 messageId,uint256 fee)",
]);
/** Client.GenericExtraArgsV2(gasLimit 0, allowOutOfOrderExecution true): public
 * wallets run no callback, so the only accepted encoding carries no delivery gas. */
export const CCIP_EXTRA_ARGS =
  "0x181dcf10" + "0".repeat(64) + "0".repeat(63) + "1";
export const ccipEvmReceiver = (address: string) =>
  ethers.AbiCoder.defaultAbiCoder().encode(
    ["address"],
    [ethers.getAddress(address)],
  );
export function ccipReceiverAddress(receiver: string): string {
  if (
    !ethers.isHexString(receiver, 32) ||
    BigInt(receiver) >> 160n !== 0n ||
    BigInt(receiver) === 0n
  )
    throw Error("Invalid CCIP receiver");
  return ethers.getAddress(ethers.dataSlice(receiver, 12));
}
function deploymentFromEnv(
  coordinator: string | undefined,
  implementation: string | undefined,
  label: string,
) {
  // A Base Sepolia deployment: the DogeOS page of the same build has none.
  if (!coordinator || ACTIVE_EVM_NETWORK_KEY === "dogeos-chikyu") return null;
  if (
    EVM_CONFIG.CHAIN_ID !== 84532 ||
    !ethers.isAddress(coordinator) ||
    !ethers.isAddress(implementation || "")
  )
    throw Error(`${label} deployment is not configured`);
  return {
    coordinator: ethers.getAddress(coordinator),
    implementation: ethers.getAddress(implementation!),
  };
}
export function privateBridgeDeployment() {
  return deploymentFromEnv(
    process.env.REACT_APP_PRIVATE_BRIDGE_COORDINATOR,
    process.env.REACT_APP_PRIVATE_BRIDGE_IMPLEMENTATION,
    "Bridge",
  );
}
export function privateCcipBridgeDeployment() {
  return deploymentFromEnv(
    process.env.REACT_APP_PRIVATE_CCIP_BRIDGE_COORDINATOR,
    process.env.REACT_APP_PRIVATE_CCIP_BRIDGE_IMPLEMENTATION,
    "CCIP bridge",
  );
}
export const anyPrivateBridgeDeployment = () =>
  privateBridgeDeployment() || privateCcipBridgeDeployment();
export function bridgeDeploymentFor(provider: PrivateBridgeProvider) {
  return provider === "ccip"
    ? privateCcipBridgeDeployment()
    : privateBridgeDeployment();
}
const escrowAddress = (
  coordinator: string,
  implementation: string,
  hash: string,
) => {
  const code =
    "0x3d602d80600a3d3981f3363d3d373d3d3d363d73" +
    implementation.slice(2).toLowerCase() +
    "5af43d82803e903d91602b57fd5bf3";
  return ethers.getCreate2Address(coordinator, hash, ethers.keccak256(code));
};
export function privateBridgeRouteHash(route: any) {
  const d = privateBridgeDeployment();
  if (!d) throw Error("Bridging is not enabled");
  return ethers.keccak256(
    ethers.AbiCoder.defaultAbiCoder().encode(
      ["uint256", "address", PRIVATE_BRIDGE_ROUTE],
      [84532, d.coordinator, route],
    ),
  );
}
export function privateBridgeEscrow(route: any) {
  const d = privateBridgeDeployment();
  if (!d) throw Error("Bridging is not enabled");
  return escrowAddress(
    d.coordinator,
    d.implementation,
    privateBridgeRouteHash(route),
  );
}
export function privateCcipBridgeRouteHash(route: any) {
  const d = privateCcipBridgeDeployment();
  if (!d) throw Error("CCIP bridging is not enabled");
  return ethers.keccak256(
    ethers.AbiCoder.defaultAbiCoder().encode(
      ["uint256", "address", PRIVATE_CCIP_BRIDGE_ROUTE],
      [84532, d.coordinator, route],
    ),
  );
}
export function privateCcipBridgeEscrow(route: any) {
  const d = privateCcipBridgeDeployment();
  if (!d) throw Error("CCIP bridging is not enabled");
  return escrowAddress(
    d.coordinator,
    d.implementation,
    privateCcipBridgeRouteHash(route),
  );
}
export const bridgeEscrowFor = (provider: PrivateBridgeProvider, route: any) =>
  provider === "ccip"
    ? privateCcipBridgeEscrow(route)
    : privateBridgeEscrow(route);
/** The relayer value for a route: Axelar delivery gas, or the bounded CCIP prepayment. */
export const bridgeRouteValue = (
  provider: PrivateBridgeProvider,
  route: any,
): bigint => BigInt(provider === "ccip" ? route.maxFee : route.gasValue);
function decodeWith(
  provider: PrivateBridgeProvider,
  iface: ethers.Interface,
  data: string,
) {
  const d = iface.parseTransaction({ data });
  if (
    !d ||
    !["execute", "recover"].includes(d.name) ||
    iface.encodeFunctionData(d.fragment, d.args).toLowerCase() !==
      data.toLowerCase()
  )
    return null;
  const { route, leg } = d.args;
  const value = bridgeRouteValue(provider, route);
  if (
    leg.settlement.recipient !== bridgeEscrowFor(provider, route) ||
    leg.settlement.token !== route.token ||
    leg.settlement.publicAmount !== route.amount ||
    Number(leg.quote.kind) !== 1 ||
    value <= 0n ||
    value > ethers.parseEther("0.1") ||
    (provider === "ccip" &&
      (route.extraArgs !== CCIP_EXTRA_ARGS ||
        ccipReceiverAddress(route.receiver) === ethers.ZeroAddress))
  )
    return null;
  return { provider, route, leg, recovery: d.name === "recover" };
}
/** Recognises calldata for whichever coordinators are configured. The selector
 * alone never decides: the full round-trip encoding and escrow binding must hold. */
export function decodePrivateBridge(data: string) {
  for (const [provider, iface, deployment] of [
    ["axelar", PRIVATE_BRIDGE_INTERFACE, privateBridgeDeployment()],
    ["ccip", PRIVATE_CCIP_BRIDGE_INTERFACE, privateCcipBridgeDeployment()],
  ] as const) {
    if (!deployment) continue;
    try {
      const decoded = decodeWith(provider, iface, data);
      if (decoded) return decoded;
    } catch {
      /* not this coordinator */
    }
  }
  return null;
}
